import { identity } from "lodash";
import { FC } from "react";
import { Field, Form } from "react-final-form";

import { Button, DesignToken, Input, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { areShortcutValid } from "helpers/canned-responses-settings";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { IAgent } from "store/entities/agents";
import { ICannedResponse } from "store/entities/canned-responses";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "store/entities/transaction/constants";
import { withVar } from "themes/helpers";

import CannedInformation from "./CannedInformation";
import { CannedRichTextEditor } from "./CannedRichTextEditor";
import { ShortcutsInfo } from "./styles";
import { FormValues } from "./types";

interface IProps {
  cannedResponse: ICannedResponse;
  agents: IAgent[];
  handleNormalMode: () => void;
  handleUpdateCannedResponse: (cannedResponseId: string) => (values: FormValues) => void;
  isCannedWithRichText: boolean;
}

const UpdateCannedResponse: FC<IProps> = ({
  cannedResponse,
  agents,
  handleNormalMode,
  handleUpdateCannedResponse,
  isCannedWithRichText,
}) => {
  const { isUploading, attachmentsSize } = useAttachments();

  return (
    <Form
      css={{ width: "100%" }}
      onSubmit={handleUpdateCannedResponse(cannedResponse.id)}
      initialValues={
        {
          cannedRichText: isCannedWithRichText
            ? cannedResponse.richTextObj
            : [
                {
                  type: "paragraph",
                  children: [
                    {
                      text: cannedResponse.text,
                    },
                  ],
                },
              ],
          cannedShortcuts: cannedResponse.shortcuts.join(", "),
        } as FormValues
      }
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render={({ handleSubmit, submitting, values }: any) => (
        <form onSubmit={handleSubmit} css={{ width: "100%" }}>
          <Flex gap={withVar(SpacingToken.Spacing6)} flexWrap width="100%">
            <Flex column gap={withVar(SpacingToken.Spacing4)} width="100%" maxWidth="580px">
              <CannedRichTextEditor
                editorKey="update-canned-response-editor"
                cannedResponseID={cannedResponse.id}
                richTextAreaMaxHeight="160px"
              />
              <Flex alignItems="center" gap="10px">
                <Text
                  fontSize="12px"
                  fontWeight="bold"
                  lineHeight="20px"
                  color={withVar(DesignToken.ContentBasicSecondary)}
                >
                  SHORTCUTS
                </Text>
                <Field
                  name="cannedShortcuts"
                  parse={identity}
                  // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  render={({ input }: any) => (
                    <Block width="100%" maxWidth="460px">
                      <Input {...input} placeholder="Enter shortcuts..." width="100%" data-testid="shortcuts-input" />
                    </Block>
                  )}
                />
              </Flex>
              <ShortcutsInfo>Separate many shortcuts with a comma.</ShortcutsInfo>
            </Flex>
            <Flex column gap="12px">
              <CannedInformation cannedResponse={cannedResponse} agents={agents} />
              <Flex gap={withVar(SpacingToken.Spacing2)}>
                <Button size="compact" kind="secondary" onClick={handleNormalMode}>
                  Cancel
                </Button>
                <Button
                  size="compact"
                  kind="primary"
                  type="submit"
                  data-testid="update-canned-response-save-button"
                  disabled={
                    submitting ||
                    isUploading() ||
                    !areShortcutValid(values.cannedShortcuts) ||
                    attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE
                  }
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </form>
      )}
    />
  );
};

export default UpdateCannedResponse;
